/**
 * Per-user (rather than per-firm) settings, backed by the OPTION store.
 *
 * ─── Why this exists ───────────────────────────────────────────────────
 *
 * `useOptions` and `OptionHelper.getOptionByLabel` both resolve a label by
 * taking the newest OPTION row with that label, regardless of who added it.
 * That is correct for firm-wide settings, but for a per-user preference it
 * would hand one user another user's value — whichever was saved last wins
 * for everybody.
 *
 * The OPTION table already models per-user scoping: `userScope = true` marks
 * a row as private to the user in `addedBy`. `Home/Dashboard/dashboardPins.ts`
 * was the first consumer and inlined its own read/write against the options
 * slice. This module is that same logic, generalised, so any preference can
 * use it without repeating the filtering rules.
 *
 * Per-user option shape:
 *   • label:     the preference name
 *   • userScope: true
 *   • addedBy:   currentUserId
 *   • type:      'string' | 'boolean' | 'numeric'
 *   • value:     stringified value
 *
 * Cross-tab sync comes free via the standard option-save broadcast in
 * sagas/optionSagas.ts → socket.io.
 */
import OptionEntity from '@/entities/option';
import type Option from '@/entities/option';
import store from '@/store/store';
import * as OptionActions from '@/store/actions/optionActions';
import {getCurrentFirmId, getCurrentUserId} from '@/lib/picos/currentLogin';
import newHRTimestamp from '@/lib/picos/timestamp';
import {genOptionUUID} from '@/lib/picos/uuids';
import type {RootState} from '@/@types/store';

/** The shape of `state.options`, derived so we stay in step with the store. */
type OptionsSlice = RootState['options'];

/** Labels of the per-user preferences. Keep them distinct from firm option names. */
export enum UserOptionNames {
    UI_FONT_FAMILY = 'uiFontFamily',
    UI_FONT_SIZE = 'uiFontSize',
    SEARCH_MODE = 'searchMode',
}

export type UserOptionType = 'string' | 'boolean' | 'numeric';

/**
 * Find the current user's row for `label`, if one exists.
 *
 * Narrows to this user's scope: `userScope === true` AND `addedBy === userId`.
 * The option store is WORM-style, so multiple versions can coexist
 * transiently — newest `addedOn` wins.
 *
 * `optionsSlice` lets a React caller pass the slice it is already subscribed
 * to, so the read participates in that component's re-render. Callers outside
 * React can omit it and we read the store directly.
 */
export function findUserOption(label: string, optionsSlice?: OptionsSlice): Option | null {
    const userId = getCurrentUserId();
    if (!userId) return null;

    const slice = optionsSlice ?? store.getState().options;
    const candidates: Option[] = slice
        .getMappingFor('label', label)
        .filter((o: Option) => o.userScope === true && o.addedBy === userId && o.status !== 'D');

    if (candidates.length === 0) return null;

    candidates.sort((a, b) => Number(b.addedOn) - Number(a.addedOn));
    return candidates[0];
}

/**
 * Read a per-user preference, falling back to `fallback` when the user has
 * never set one.
 *
 * Forgiving by design: a preference is a nicety, and a malformed stored value
 * should degrade to the default rather than throw somewhere up the render
 * tree.
 */
export function getUserOptionValue<T extends string | number | boolean>(
    label: string,
    fallback: T,
    optionsSlice?: OptionsSlice,
): T {
    const option = findUserOption(label, optionsSlice);
    if (!option || option.value == null || option.value === '') return fallback;

    switch (option.type) {
        case 'numeric': {
            const n = Number.parseFloat(option.value);
            return (Number.isFinite(n) ? n : fallback) as T;
        }
        case 'boolean':
            return (typeof option.value === 'string'
                ? option.value.toLowerCase().startsWith('t')
                : !!option.value) as T;
        default:
            return option.value as T;
    }
}

/**
 * Write a per-user preference through the standard option-save queue, which
 * handles offline buffering and cross-tab sync.
 *
 * Reuses the existing `optionId` so repeated changes update one row rather
 * than accumulating a new row per change. A never-saved option's ID comes from
 * the firm, the user and the label, so every tab and reload uses the same row
 * before the first save reaches the store (OWA-915).
 */
export function setUserOption(
    label: string,
    value: string | number | boolean,
    type: UserOptionType = 'string',
): Option | null {
    const userId = getCurrentUserId();
    if (!userId) return null;

    const existing = findUserOption(label);

    const option = new OptionEntity({
        ...(existing ?? {}),
        optionId: existing?.optionId ?? genOptionUUID(getCurrentFirmId(), `${userId}:${label}`),
        firmId: getCurrentFirmId(),
        addedBy: userId,
        addedOn: newHRTimestamp(),
        userScope: true,
        type,
        label,
        value: `${value}`,
        status: existing ? 'U' : 'C',
    });
    // A plain save, even when `existing` is a queued restore: the server appends after a delete (OWA-1294)
    option.restore = false;

    store.dispatch(OptionActions.saveOptionQueue(option));

    return option;
}
